import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTheme } from '../../theme';

ChartJS.register(ArcElement, Tooltip, Legend);

function readCssVar(name, fallback) {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

const DEFAULT_LABELS = ['Passed', 'Failed', 'Pending'];

export default function TestStatusChart({ data, totalTestCases, labels = DEFAULT_LABELS }) {
  const chartRef = useRef(null);
  const { resolvedTheme } = useTheme();
  // data-theme is applied in ThemeProvider's effect, after this render; re-read CSS vars on the next one.
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    setThemeTick((n) => n + 1);
  }, [resolvedTheme]);

  const total = Number(data?.[0]) || Number(totalTestCases) || 0;
  const colors = useMemo(
    () => ({
      success: readCssVar('--success', '#10B981'),
      danger: readCssVar('--danger', '#EF4444'),
      warning: readCssVar('--warning', '#F59E0B'),
      muted: readCssVar('--text-muted', '#94A3B8'),
      surface: readCssVar('--surface', '#FFFFFF'),
      text: readCssVar('--text-primary', '#0F172A'),
      tooltipBg: readCssVar('--chart-tooltip-bg', '#0F172A'),
      tooltipText: readCssVar('--chart-tooltip-text', '#F8FAFC'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeTick]
  );

  const formatPercentage = (value) => {
    if (!total) return '0%';
    const pct = (Number(value) / total) * 100;
    const isWhole = Math.abs(pct - Math.round(pct)) < 1e-9;
    if (isWhole) return `${Math.round(pct)}%`;
    return `~${pct.toFixed(2)}%`;
  };

  const chartData = {
    labels,
    datasets: [
      {
        data: data?.slice(1, 4) || [0, 0, 0],
        backgroundColor: [colors.success, colors.danger, colors.warning],
        borderColor: [colors.surface, colors.surface, colors.surface],
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '74%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.tooltipBg,
        titleColor: colors.tooltipText,
        bodyColor: colors.tooltipText,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) =>
            ` ${ctx.label}: ${ctx.raw.toLocaleString()} (${formatPercentage(ctx.raw)})`,
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', height: '100%', minHeight: 200 }}>
      <Doughnut ref={chartRef} data={chartData} options={options} />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
          {total.toLocaleString()}
        </div>
        <div style={{ fontSize: 11, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Tests
        </div>
      </div>
    </div>
  );
}
